import {
  BranchStepTypes,
  type Step,
} from 'pages/AutomationEngine/types/automation';
import { edgeLabels } from 'pages/AutomationEngine/steps';
import {
  isConditionOrGoalSplit,
  isGoalOrCondition,
  isBranchStep,
  isBranchSplit,
} from 'pages/AutomationEngine/store/ts-utilities';
import { CSSProperties } from 'react';
import { type Edge } from 'reactflow';
import { NODE_TYPES } from './conatants';

type Leaf = {
  sourceStep: Step | { id: string };
  branches: string[];
};

export type Label = {
  text: string;
  type?: string;
  goalText?: string;
  leaf?: boolean;
  branch?: boolean;
} | null;

export const unpositioned = { x: -1, y: -1 };

export const isYesStep = (step: Step) => step.fromLabel === edgeLabels.yes.type;

export const isNoStep = (step: Step) => step.fromLabel === edgeLabels.no.type;

export const createEdge = (
  source: string,
  target: string,
  label?: Label,
  goalType: boolean = false,
  stroke?: string,
  sourceHandle: string = 'handle-right',
  targetHandle: string = 'handle-left',
  style?: CSSProperties,
  endOfBranch?: boolean
): Edge => {
  return {
    id: `${source}-${target}`,
    type: 'basic',
    sourceHandle,
    targetHandle,
    source,
    target,
    label: label && (goalType ? label.goalText : label.text),
    data: { ...label, endOfBranch },
    style: { stroke, ...style },
  };
};

export const isEdge = (obj: any): obj is Edge =>
  'source' in obj && 'target' in obj;

export const nodeFromStep = (step: Step) => ({
  id: step.id,
  type: NODE_TYPES.automationStep,
  data: step,
  position: unpositioned,
  className: 'nopan',
});

export const nodeFromTrigger = <T extends Pick<Step, 'id'> = Step>(
  trigger: T
) => ({
  id: trigger.id,
  type: NODE_TYPES.automationTrigger,
  data: trigger,
  position: unpositioned,
});

export const nodeFromBranch = (step: Step) => ({
  id: step.id,
  type: NODE_TYPES.branchNode,
  data: step,
  position: unpositioned,
  className: 'nopan',
});

export const nodeFromBranchSplit = (step: Step) => ({
  id: step.id,
  type: NODE_TYPES.branchSplit,
  data: step,
  position: unpositioned,
  className: 'nopan',
});

export const nodeForAdditionalAdd = () => {
  return {
    id: `connection.additional-variables`,
    type: NODE_TYPES.additionalVariables,
    position: unpositioned,
    data: { leaf: true, branch: false, id: null, parentKey: null, label: null },
  };
};

export const nodeForUpload = (index = 0, isLeaf = false) => {
  return {
    id: `new.upload.${index}`,
    type: NODE_TYPES.upload,
    position: unpositioned,
    data: {
      leaf: isLeaf,
      branch: false,
      id: null,
      parentKey: null,
      label: null,
      index,
    },
  };
};

export const nodeForTransformAdd = () => ({
  id: `transform.add`,
  type: NODE_TYPES.transformAdd,
  position: unpositioned,
  data: { leaf: true, branch: false, id: null, parentKey: null, label: null },
});

export const edgeFromTriggerToAdditionalVariables = ({
  id,
}: Pick<Step, 'id'>) => createEdge(id, `connection.additional-variables`);

export const edgeFromAdditionalVariablesToAdd = ({ id }: Pick<Step, 'id'>) =>
  createEdge(`connection.additional-variables`, id);

export const hiddenForMove = (stepInMovingBranch: Step) => {
  return stepInMovingBranch;
};

const checkForBranchSplitBranchLeaf = (
  current: Step,
  branches: Record<string, any>
): Leaf | null => {
  const branchId = Object.keys(branches).find((key) => key === current.id);
  if (!branchId) {
    return null;
  }
  return {
    sourceStep: { id: `${branchId}.branch` },
    branches: [''],
  };
};

const findTheEnds = (
  tree: Step[],
  parentKey: string,
  branches: Record<string, any>
) => {
  if (!tree) {
    return [];
  }

  const root = tree.find((step) => step.id === parentKey);
  if (!root) {
    return [];
  }

  const leaves: Leaf[] = [];
  const queue = [root];

  while (queue.length > 0) {
    const current = queue.shift();
    if (current) {
      let children = tree.filter(
        (step) =>
          step.parentKey === current.id && step.type !== BranchStepTypes.MERGE
      );

      if (!children || children.length === 0) {
        if (isConditionOrGoalSplit(current)) {
          leaves.push({
            sourceStep: current,
            branches: ['yes', 'no'],
          });
        } else {
          leaves.push({
            sourceStep: current,
            branches: [''],
          });

          if (isBranchSplit(current)) {
            const branchLeaf = checkForBranchSplitBranchLeaf(current, branches);
            if (branchLeaf) {
              leaves.push(branchLeaf);
            }
          }
        }
      } else {
        if (
          (isGoalOrCondition(current) || isBranchStep(current)) &&
          current.isBranchGroupInitiator &&
          current.id !== parentKey
        ) {
          // going to skip this whole merged branch
          const sourceStep = tree.find(
            (step) =>
              step.parentKey === current.id &&
              step.type === BranchStepTypes.MERGE
          );

          if (sourceStep) {
            children = tree.filter(
              (step) =>
                step.parentKey === sourceStep.id &&
                step.type !== BranchStepTypes.MERGE
            );

            if (!children.length) {
              // no children of merge then the merge is the end
              leaves.push({ sourceStep, branches: [''] });
            }
          }
        } else if (isConditionOrGoalSplit(current)) {
          const yeses = children.some(isYesStep);
          const noes = children.some(isNoStep);

          if (yeses && !noes) {
            leaves.push({
              sourceStep: current,
              branches: ['no'],
            });
          } else if (noes && !yeses) {
            leaves.push({
              sourceStep: current,
              branches: ['yes'],
            });
          }
        } else if (isBranchSplit(current)) {
          const branchLeaf = checkForBranchSplitBranchLeaf(current, branches);
          if (branchLeaf) {
            leaves.push(branchLeaf);
          }
        }

        queue.push(...children);
      }
    }
  }

  return leaves;
};

export const getMergeEnds = (tree: Step[], branches: Record<string, any>) => {
  const branchGroupInitiatorNodes = tree.filter(
    (s) => s.isBranchGroupInitiator
  );

  if (branchGroupInitiatorNodes.length === 0) {
    return [];
  }

  const ends = branchGroupInitiatorNodes.reduce<
    { endNodes: Leaf[]; join: Step | undefined }[]
  >((currentBGI, bgi) => {
    const endNodes = findTheEnds(tree, bgi.id, branches);
    const join = tree.find(
      (step) => step.parentKey === bgi.id && step.type === BranchStepTypes.MERGE
    );
    currentBGI.push({
      endNodes,
      join,
    });

    return currentBGI;
  }, []);

  return ends;
};
